import { Component, DestroyRef, inject, OnDestroy, OnInit, Renderer2 } from '@angular/core';
import { Action, select, Store } from '@ngrx/store';
import { selectStreamsWithVideo } from '../../../event/features/event.selectors';
import { SharedService } from '../../../virtual-event/services/shared.service';
import { loadAllStreams, loadVideoDownloadUrl, loadVideoDownloadUrlSuccess } from '../../../event/features/event.actions';
import { IPaybeeVideoModal, IRecording, IStreamInfo } from '../../../event/models/stream-model';
import * as DialogActions from '../../../dialog/features/dialog.actions';
import { Actions, ofType } from '@ngrx/effects';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

type IVideoUrlResponse = {
  data: {
    downloadExpiresOn: Date,
    downloadUrl: string
  };
  type: string;
  payload?: {
    recordingId: string,
    streamId: string,
    onlyLink?: boolean,
    index?: number;
    recordingIdx?: number;
  };
};

@Component({
  selector: 'app-paybee-livestream',
  templateUrl: './paybee-livestream.component.html',
  styleUrls: ['./paybee-livestream.component.scss']
})
export class PaybeeLivestreamComponent implements OnInit, OnDestroy {
  destroyRef = inject(DestroyRef)
  public loadAllStreams$ = this.store.pipe(
    select(selectStreamsWithVideo)
  );

  public currentStream: IStreamInfo;
  public currentRecording: IRecording;
  public allStreams: IStreamInfo[];

  constructor(
    public store: Store,
    public sharedService: SharedService,
    public updates$: Actions,
    private renderer: Renderer2
  ) {
  }

  ngOnInit(): void {
    this.store.dispatch({
      type: loadAllStreams.type,
      payload: {
        id: this.sharedService.token,
      }
    });

    this.loadAllStreams$.pipe(
      takeUntilDestroyed(this.destroyRef)
    ).subscribe((streams: IStreamInfo[]) => {
      if (streams) {
        this.allStreams = streams;
      }
    });

    this.updates$.pipe(
      ofType(
        loadVideoDownloadUrlSuccess.type
      ),
      takeUntilDestroyed(this.destroyRef)
    )
    .subscribe((response: IVideoUrlResponse) => {
        const {index, recordingIdx} = response.payload;
        this.allStreams[index].recordings[recordingIdx].download = response.data;

        if (response.payload.onlyLink) {
          this.onDownloadVideo(response.data.downloadUrl);
          return;
        }

        this.onShowVideoModal(response.data.downloadUrl, response.data.downloadExpiresOn);
    });
  }

  ngOnDestroy(): void {
    this.currentStream = null;
  }

  getId(index: number, item: IStreamInfo): string {
    return item.id;
  }

  getRecordingId(index: number, item: IRecording): number {
    return item.recordingId;
  }

  onShowVideo(recording: IRecording, streamInfo: IStreamInfo, index: number, recordingIdx: number): void {
    event.stopPropagation();
    event.preventDefault();

    this.currentStream = streamInfo;
    this.currentRecording = recording;

    if (!recording.download.downloadExpiresOn || !recording.download.downloadUrl) {
      this.store.dispatch({
        type: loadVideoDownloadUrl.type,
        payload: {
          recordingId: recording.recordingId,
          streamId: streamInfo.id,
          index,
          recordingIdx
        }
      });

      return;
    }

    this.onShowVideoModal(recording.download.downloadUrl, recording.download.downloadExpiresOn);
  }

  formatTime(timestamp): string {
    const secNum = parseInt(timestamp, 10); // don't forget the second param
    let hours: string | number = Math.floor(secNum / 3600);
    let minutes: string | number = Math.floor((secNum - (hours * 3600)) / 60);
    let seconds: string | number = secNum - (hours * 3600) - (minutes * 60);

    if (hours < 10) {
      hours = '0' + hours;
    }
    if (minutes < 10) {
      minutes = '0' + minutes;
    }
    if (seconds < 10) {
      seconds = '0' + seconds;
    }
    return hours + ':' + minutes + ':' + seconds;
  }

  formatBytes(bytes: number, decimals: number = 2): string {
    if (bytes === 0) {
      return '0 Bytes';
    }

    const k = 1024;
    const dm = decimals < 0 ? 0 : decimals;
    const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));

    return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + ' ' + sizes[i];
  }

  onLinkClick(event: MouseEvent, recording: IRecording, streamInfo: IStreamInfo, index: number, recordingIdx: number): void {
    if (!recording.download.downloadUrl) {
      this.store.dispatch({
        type: loadVideoDownloadUrl.type,
        payload: {
          recordingId: recording.recordingId,
          streamId: streamInfo.id,
          onlyLink: true,
          index,
          recordingIdx
        }
      });

      return;
    }
    this.onDownloadVideo(recording.download.downloadUrl);
  }

  onDownloadVideo(downloadUrl: string): void {
    const link = this.renderer.createElement('a');
    link.setAttribute('href', downloadUrl);
    link.click();
    link.remove();
  }

  onShowVideoModal(downloadUrl: string, downloadExpiresOn: Date): void {
    const initialState: IPaybeeVideoModal = {
      streamName: this.currentStream.virtualEventInfo.name,
      recordedOn: this.currentRecording.recordedOn,
      size: this.currentRecording.metadata.size,
      src: downloadUrl,
      downloadExpiresOn
    };

    const action: Action = DialogActions.openDialog({
      payload: {
        name: 'paybeeVideo',
        options: {
          size: 'large',
          modalClass:'modal--paybee modal-lg',
          containerClass:'mdb-modal-backdrop-paybee'
        },
        initialState
      }
    });

    this.store.dispatch(action);
  }
}
